import { useApiEnvironment } from "@mirage/service-auth/useApiEnvironment";
import useDropboxAccount from "@mirage/service-auth/useDropboxAccount";
import { useFeatureFlagValue } from "@mirage/service-experimentation/useFeatureFlagValue";
import { convertFeatureValueToBool } from "@mirage/service-experimentation/util";
import { SurveyProvider } from "@mirage/service-feedback/hooks/SurveyProvider";
import { logPageLoadMilestoneOnce } from "@mirage/service-operational-metrics/page-load";
import { SearchContextProvider } from "@mirage/service-search/hooks/useSearchContext";
import { privacyCategoryStateAtom } from "@mirage/shared/privacy";
import { activeStackAtom } from "@mirage/stacks/ActiveStack/atoms";
import { getIsPublicStack } from "@mirage/stacks/utils";
import { StartPageFocusStateLoader } from "@mirage/webapp/analytics/sessionManagers/StartPageFocusStateLoader";
import { WebLayout } from "@mirage/webapp/components/WebLayout";
import { getEligibilityFromCache } from "@mirage/webapp/gating/Eligibility";
import { WebEligibilityCheck } from "@mirage/webapp/gating/WebEligibilityCheck";
import { useInitAuthedJobs } from "@mirage/webapp/hooks/useInitAuthedJobs";
import { isConsentAccepted } from "@mirage/webapp/hooks/usePrivacyModule";
import { PageType } from "@mirage/webapp/routeTypes";
import { useAtomValue } from "jotai";

import { memo } from "react";
import { useRoute } from "../Routes";
import { sharedWorkerDatabase } from "../shared-worker/sharedWorkerStorage";
import { Config } from "../shared/config";

function getEligibilityFromStore() {
  return getEligibilityFromCache(sharedWorkerDatabase);
}

export const AuthedContainer: React.FC = memo(function AuthedContainer() {
  logPageLoadMilestoneOnce("AuthedContainer start");

  const route = useRoute();
  const account = useDropboxAccount();
  const privacyCategoryState = useAtomValue(privacyCategoryStateAtom);
  const isAnalyticsEnabled = isConsentAccepted(privacyCategoryState);

  const activeStack = useAtomValue(activeStackAtom);
  const isPublicStack = getIsPublicStack(activeStack);

  const isStackRemoveEligibilityCheckEnabled = convertFeatureValueToBool(
    useFeatureFlagValue("dash_web_2025_02_07_stack_remove_eligibility_check"),
  );

  // this is a meta-feature that is used as a prerequisite for other April launch features as a way to A/B test it
  // we need the exposure logging here, but don't need to use the value
  useFeatureFlagValue("dash_2025_04_21_april_rollout", true);

  useInitAuthedJobs(route.pattern, Config.DEVBOX);
  useApiEnvironment();

  if (route.pageType === PageType.LOGOUT) {
    return <route.Component />;
  }

  const shouldCheckWebEligibility = isStackRemoveEligibilityCheckEnabled
    ? route.pageType !== PageType.STACK
    : route.pageType !== PageType.STACK || isPublicStack === false;

  return (
    <>
      <SurveyProvider
        surface="webapp"
        environmentId={Config.SPRIG_ENVIRONMENT_ID}
        disabled={!isAnalyticsEnabled}
        publicAccountId={
          account?.public_account_id && atob(account?.public_account_id)
        }
      />
      <SearchContextProvider isLocalFilesEnabled={false}>
        {shouldCheckWebEligibility && (
          <WebEligibilityCheck
            getEligibilityFromStore={getEligibilityFromStore}
            isDevboxBackend={Config.ENV === "devbox"}
          />
        )}

        <StartPageFocusStateLoader />

        {route.wholePage ? (
          <route.Component />
        ) : (
          <WebLayout hideTopNav={route.hideTopNav}>
            <route.Component />
          </WebLayout>
        )}
      </SearchContextProvider>
    </>
  );
});
